import type { SessionSettings } from '@/types/chat'
import { useEffect, useState } from 'react'
import { SystemPromptEditor } from '@/components/chat/SystemPromptEditor'
import { promptTemplatesApi, type PromptTemplate } from '@/lib/api/prompt-templates'
import { cn } from '@/lib/utils'

interface ChatSettingsPanelProps {
  settings: SessionSettings
  systemPrompt: string
  onSettingsChange: (patch: Partial<SessionSettings>) => void
  onSystemPromptChange: (value: string) => void
  onSystemPromptBlur?: () => void
  useTextareaFallback?: boolean
  className?: string
}

export function ChatSettingsPanel({
  settings,
  systemPrompt,
  onSettingsChange,
  onSystemPromptChange,
  onSystemPromptBlur,
  useTextareaFallback = false,
  className,
}: ChatSettingsPanelProps) {
  const [templates, setTemplates] = useState<PromptTemplate[]>([])

  useEffect(() => {
    promptTemplatesApi.list().then(setTemplates).catch(() => setTemplates([]))
  }, [])

  return (
    <aside
      className={cn('flex h-full flex-col gap-3 overflow-auto border-r border-border bg-card p-3', className)}
      data-testid="chat-settings-panel"
    >
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Model Settings</p>

      <label className="grid gap-1 text-sm">
        <span className="text-muted-foreground">Temperature</span>
        <input
          type="number"
          min={0}
          max={2}
          step={0.1}
          value={settings.temperature ?? 0.7}
          onChange={(event) => onSettingsChange({ temperature: Number(event.target.value) })}
          className="rounded-md border border-input bg-background px-2 py-1"
          data-testid="chat-setting-temperature"
        />
      </label>

      <label className="grid gap-1 text-sm">
        <span className="text-muted-foreground">Top P</span>
        <input
          type="number"
          min={0}
          max={1}
          step={0.05}
          value={settings.topP ?? 0.9}
          onChange={(event) => onSettingsChange({ topP: Number(event.target.value) })}
          className="rounded-md border border-input bg-background px-2 py-1"
        />
      </label>

      <label className="grid gap-1 text-sm">
        <span className="text-muted-foreground">Max tokens</span>
        <input
          type="number"
          min={256}
          max={128000}
          step={256}
          value={settings.maxTokens ?? 4096}
          onChange={(event) => onSettingsChange({ maxTokens: Number(event.target.value) })}
          className="rounded-md border border-input bg-background px-2 py-1"
        />
      </label>

      <label className="inline-flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={settings.jsonMode ?? false}
          onChange={(event) => onSettingsChange({ jsonMode: event.target.checked })}
        />
        JSON mode
      </label>

      <div className="grid gap-1">
        <span className="text-sm text-muted-foreground">System prompt</span>
        {templates.length > 0 ? (
          <label className="grid gap-1 text-sm">
            <span className="text-xs text-muted-foreground">Apply template</span>
            <select
              defaultValue=""
              onChange={(event) => {
                const template = templates.find((item) => item.id === event.target.value)
                if (template) onSystemPromptChange(template.content)
              }}
              className="rounded-md border border-input bg-background px-2 py-1 text-sm"
              data-testid="chat-prompt-template-select"
            >
              <option value="">Choose template…</option>
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <SystemPromptEditor
          value={systemPrompt}
          onChange={onSystemPromptChange}
          onBlur={onSystemPromptBlur}
          useTextareaFallback={useTextareaFallback}
          height="180px"
        />
      </div>
    </aside>
  )
}
